import { useAction, useAtom } from '@reatom/npm-react'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { css } from 'styled-components'

import { AsyncStateN } from '@blade/std-lib'
import { Button } from '@blade/ui-kit'
import { Network } from '@root/entities/network'
import { CommentIcon, InfoMessage } from '@root/shared/ui-kit'

import { CommentsThreadMessage } from './comments-thread-message'

import type { Pair } from '@root/entities/pair'
import type { CommentsModel } from '@root/features/comment/atoms'
import type { ClassNameProps } from '@root/shared/react'

export type CommentsThreadEmptyProps = {
  model: CommentsModel
  pair: Pair.PairMinimal
  ref?: React.Ref<HTMLDivElement>
} & ClassNameProps

export const CommentsThreadEmpty = ({
  className,
  pair,
  model,
  ref,
}: CommentsThreadEmptyProps) => {
  const { t } = useTranslation()
  const [image, setImage] = useState<string | undefined>(undefined)

  const [messageOpen, setMessageOpen] = useAtom(model.replyCommentIdAtom)

  const createCommentAction = useAction(model.createCommentAction)
  const [createCommentRequestState] = useAtom(model.createCommentRequestState)
  const handleMessageShow = () => setMessageOpen('root')
  const handleMessageHide = () => setMessageOpen(undefined)
  const handleMessageSend = (text: string) => {
    createCommentAction({
      pairAddress: pair.address,
      req: {
        content: text,
        network: Network.toDto(pair.network),
        imageAttachment: image,
      },
    })
  }

  return (
    <Root className={className} ref={ref}>
      <InfoMessage
        intent="empty"
        variant="dt"
        withBackground
        icon={<CommentIcon />}
        title={t('comment.no_comments_title')}
        description={t('comment.no_comments_description')}
        retryText={t('comment.add_comment')}
        withSeparator
        css={css`
          height: 100%;
          padding: 12px;
        `}
      >
        {messageOpen === 'root' ? (
          <CommentsThreadMessageEmpty
            open
            isLoading={AsyncStateN.isLoading(createCommentRequestState)}
            onMessageSend={handleMessageSend}
            onMessageShow={handleMessageShow}
            onClose={handleMessageHide}
            imageAttachment={image}
            onImageAttachedChange={setImage}
          />
        ) : (
          <ButtonAddComment
            size="xs"
            color="primary"
            variant="solid"
            radius="rounded"
            onClick={handleMessageShow}
          >
            {t('comment.add_comment')}
          </ButtonAddComment>
        )}
      </InfoMessage>
    </Root>
  )
}

const Root = styled.div``

const ButtonAddComment = styled(Button)`
  height: 28px;
  font-weight: 500;
`

const CommentsThreadMessageEmpty = styled(CommentsThreadMessage)`
  width: 600px;
  background-image: none;
`
